import { notifications } from "@mantine/notifications";
import type { QueryClient } from "@tanstack/react-query";

import type { TFunction } from "./requestErrorText";
import { ApiError } from "../../api/apiError";

const HTTP_CONFLICT = 409;
const QUEUE_RETIRED = "queue_retired";

// 409 `queue_retired`: очередь задачи снята с учёта (код только у перезапуска).
// Состояние задачи не менялось, перечитывать нечего — причина показывается в
// окне действия.
export function isQueueRetired(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === HTTP_CONFLICT &&
    error.code === QUEUE_RETIRED
  );
}

// 409 на действии над задачей = «состояние задачи уже изменилось».
// `queue_retired` — не конфликт состояния (см. `isQueueRetired`).
export function isTaskConflict(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === HTTP_CONFLICT &&
    !isQueueRetired(error)
  );
}

// Перечитывание всего, что показывает состояние задачи: сама задача, лог
// переходов, список и активная задача очереди. Зовётся после действия над
// задачей (успех) и после отказа 409 («состояние уже изменилось»).
// Инвалидация по смысловому префиксу матчит запись любого org-скоупа (см.
// `src/query/scope.ts`).
export function invalidateTaskViews(
  queryClient: QueryClient,
  taskId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: ["task", taskId] });
  void queryClient.invalidateQueries({ queryKey: ["task-events", taskId] });
  void queryClient.invalidateQueries({ queryKey: ["tasks"] });
  void queryClient.invalidateQueries({ queryKey: ["queue"] });
}

// Отказ действия над задачей на уровне хука мутации: колбэки `useMutation`
// срабатывают и после размонтирования кнопки (статус задачи сменился, баннер
// или секция действий пропали до ответа сервера), колбэк `mutate` — нет. На
// 409 (кроме `queue_retired`) перечитываем задачу и показываем уведомление:
// текст в окне кнопки пропал бы вместе с ней.
export function handleTaskActionError(
  queryClient: QueryClient,
  taskId: string,
  error: unknown,
  t: TFunction,
): void {
  if (isTaskConflict(error)) {
    invalidateTaskViews(queryClient, taskId);
    notifications.show({ message: t("task.actionConflict"), color: "yellow" });
  }
}
